<template>
  <section
    class="relative h-[600px] md:h-[700px] bg-cover bg-center flex items-center justify-center text-white overflow-hidden transition-all duration-700 ease-in-out"
    :style="{ backgroundImage: `url(${currentSlide.image})` }"
  >
    <!-- Overlay -->
    <div class="absolute inset-0 bg-[#0000009b] bg-opacity-40"></div>

    <!-- Hero Content -->
    <div class="relative z-10 text-center px-4">
      <h1 class="text-4xl md:text-6xl font-bold mb-4 animate-fade-in-up">
        <span v-html="currentSlide.title"></span>
      </h1>
      <p class="text-lg md:text-xl mb-8 max-w-2xl mx-auto animate-fade-in-up delay-100">
        {{ currentSlide.description }}
      </p>
      <div class="flex flex-col sm:flex-row gap-4 justify-center animate-fade-in-up delay-200">
        <button class="
      
          bg-white text-gray-900 cursor-pointer border border-white px-8 py-3 rounded-full font-semibold hover:bg-gray-200 transition-all
        ">
          Get Started
        </button>
        <button class="bg-white text-gray-900 cursor-pointer border border-white px-8 py-3 rounded-full font-semibold hover:bg-gray-200 transition-all">
          Learn More
        </button>
      </div>
    </div>

    <!-- Hero Carousel Navigation Arrows -->
    <button
      @click="prevSlide"
      class="absolute left-4 top-1/2 -translate-y-1/2 bg-gray-800/70 hover:bg-opacity-90 text-white p-3 rounded-full z-20 hidden md:block"
      aria-label="Previous slide"
    >
      <ChevronLeft class="h-6 w-6" />
    </button>
    <button
      @click="nextSlide"
      class="absolute right-4 top-1/2 -translate-y-1/2 bg-gray-800/70 hover:bg-opacity-90 text-white p-3 rounded-full z-20 hidden md:block"
      aria-label="Next slide"
    >
      <ChevronRight class="h-6 w-6" />
    </button>

    <!-- Carousel Indicators/Dots -->
    <div class="absolute bottom-20 left-1/2 transform -translate-x-1/2 flex space-x-2 z-20">
      <button
        v-for="(slide, index) in slides"
        :key="index"
        @click="goToSlide(index)"
        :class="[
          'w-3 h-3 rounded-full transition-all duration-200',
          currentSlideIndex === index 
            ? 'bg-[#FDB813] scale-125' 
            : 'bg-white bg-opacity-50 hover:bg-opacity-75'
        ]"
        :aria-label="`Go to slide ${index + 1}`"
      />
    </div>

    <!-- Feature Cards Carousel (Responsive with Scroll Snap) -->
    <div class="absolute bottom-0 w-full px-4 md:px-8 lg:px-16 z-20 mb-4 sm:mb-6">
      <div class="overflow-x-auto scroll-smooth snap-x snap-mandatory pb-4 -mx-2">
        <div class="flex gap-4 px-2">
          <div
            v-for="(feature, index) in featureCards"
            :key="index"
            :style="{ backgroundColor: feature.color }"
            class="flex-shrink-0 snap-center cursor-pointer p-4 rounded-lg text-white text-center shadow-lg transition-all duration-300 hover:scale-105 hover:shadow-xl
                   w-full sm:w-[calc(50%-0.5rem)] md:w-[calc(33.333%-0.66rem)] lg:w-[calc(25%-0.75rem)] xl:w-[calc(20%-0.8rem)]"
          >
            <component :is="iconComponents[feature.icon]" :size="36" class="mb-2 mx-auto" />
            <h3 class="text-sm md:text-base font-semibold">{{ feature.title }}</h3>
          </div>
        </div>
      </div>
    </div>

    <!-- Auto-play Controls -->
   
  </section>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'

import {
  ChevronLeft, ChevronRight, ShoppingCart, Settings, Globe,
  LineChart, BookOpen, Camera, HeartPulse, GraduationCap, Book,
  Play, Pause
} from 'lucide-vue-next'
import carousel1 from '../../assets/carousel1.jpg'
import carousel2 from '../../assets/carousel2.jpg'
import carousel3 from '../../assets/carousel3.jpg'


// Hero slides data
const slides = ref([
  {
    image: carousel1,
    title: 'BEST <span class="text-[#FDB813]">ONLINE TRAINING</span> COURSE',
    description: 'Contrary to popular belief, Lorem Ipsum is not simply random',
  },
  {
    image: carousel2,
    title: 'LEARN <span class="text-[#FDB813]">ANYTIME</span> ANYWHERE',
    description: 'Access high-quality education from the comfort of your home.',
  },
  {
    image: carousel3,
    title: 'GLOBAL <span class="text-[#FDB813]">COMMUNITY</span> OF LEARNERS',
    description: 'Join a vibrant network of students and educators worldwide.',
  },
])

const currentSlideIndex = ref(0)
const currentSlide = computed(() => slides.value[currentSlideIndex.value])

// Auto-play functionality
const isAutoPlaying = ref(true)
const autoPlayInterval = ref(null)
const autoPlayDelay = 5000 // 5 seconds

// Start auto-play
const startAutoPlay = () => {
  if (autoPlayInterval.value) return
  
  autoPlayInterval.value = setInterval(() => {
    nextSlide()
  }, autoPlayDelay)
}

// Stop auto-play
const stopAutoPlay = () => {
  if (autoPlayInterval.value) {
    clearInterval(autoPlayInterval.value)
    autoPlayInterval.value = null
  }
}

// Toggle auto-play
const toggleAutoplay = () => {
  isAutoPlaying.value = !isAutoPlaying.value
  if (isAutoPlaying.value) {
    startAutoPlay()
  } else {
    stopAutoPlay()
  }
}

// Carousel navigation functions
const nextSlide = () => {
  currentSlideIndex.value = (currentSlideIndex.value + 1) % slides.value.length
}

const prevSlide = () => {
  // Stop auto-play temporarily when manually navigating
  if (isAutoPlaying.value) {
    stopAutoPlay()
    setTimeout(() => {
      if (isAutoPlaying.value) startAutoPlay()
    }, 3000) // Resume after 3 seconds
  }
  
  currentSlideIndex.value =
    (currentSlideIndex.value - 1 + slides.value.length) % slides.value.length
}

const goToSlide = (index) => {
  // Stop auto-play temporarily when manually navigating
  if (isAutoPlaying.value) {
    stopAutoPlay()
    setTimeout(() => {
      if (isAutoPlaying.value) startAutoPlay()
    }, 3000) // Resume after 3 seconds
  }
  
  currentSlideIndex.value = index
}

// Handle manual next slide click
const handleNextSlide = () => {
  // Stop auto-play temporarily when manually navigating
  if (isAutoPlaying.value) {
    stopAutoPlay()
    setTimeout(() => {
      if (isAutoPlaying.value) startAutoPlay()
    }, 3000) // Resume after 3 seconds
  }
  
  nextSlide()
}

// Feature Cards data
const featureCards = ref([
  { icon: 'Globe', title: 'Scholarship', color: '#F44369' }, // Pink/Red
  { icon: 'LineChart', title: '2500+ Courses', color: '#A8E063' }, // Light Green
  { icon: 'BookOpen', title: 'Admission', color: '#FFC107' }, // Yellow/Orange
  { icon: 'Camera', title: 'Global Exposure', color: '#E91E63' }, // Hot Pink
  { icon: 'HeartPulse', title: 'Academics', color: '#00BCD4' }, // Light Blue/Cyan
  { icon: 'GraduationCap', title: 'Certifications', color: '#673AB7' },
  { icon: 'Book', title: 'Library Access', color: '#4CAF50' },
])

const iconComponents = {
  ChevronLeft, ChevronRight, ShoppingCart, Settings, Globe,
  LineChart, BookOpen, Camera, HeartPulse, GraduationCap, Book,
  Play, Pause
}

// Lifecycle hooks
onMounted(() => {
  // Start auto-play when component mounts
  if (isAutoPlaying.value) {
    startAutoPlay()
  }
  
  // Pause auto-play when page is not visible
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) {
      stopAutoPlay()
    } else if (isAutoPlaying.value) {
      startAutoPlay()
    }
  })
})

onUnmounted(() => {
  // Clean up interval when component unmounts
  stopAutoPlay()
})

// Update the nextSlide function call in template
const nextSlide_updated = handleNextSlide
</script>

<style scoped>
/* Animation for hero content */
.animate-fade-in-up {
  animation: fadeInUp 0.6s ease-out forwards;
}

.animate-fade-in-up.delay-100 {
  animation-delay: 0.1s;
}

.animate-fade-in-up.delay-200 {
  animation-delay: 0.2s;
}

@keyframes fadeInUp {
  0% {
    opacity: 0;
    transform: translateY(20px);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Hide scrollbar for the feature cards carousel */
.overflow-x-auto::-webkit-scrollbar {
  display: none;
}
.overflow-x-auto {
  -ms-overflow-style: none;  /* IE and Edge */
  scrollbar-width: none;  /* Firefox */
}

/* Smooth transition for background image changes */
.transition-all {
  transition: background-image 0.7s ease-in-out;
}
</style>